namespace $.$$ {

	export class $shm_ba_date extends $.$shm_ba_date {

		date_min() {
			return this.period_all().from
		}

		date_max() {
			return this.period_all().to
		}

		period_all() {
			return super.period_all() as $shm_ba_data_period
		}

		date_text( date: Date ) {
			const year = date.getFullYear()
			const month = String( date.getMonth() + 1 ).padStart( 2, '0' )
			const day = String( date.getDate() ).padStart( 2, '0' )
			return `${ year }-${ month }-${ day }`
		}

		date_day( date: string ) {
			return date.slice( 0, 10 )
		}

		date_time( date: string ) {
			const match = date.match( /^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})/ )
			if( !match ) return ''
			return `${ match[1] }:${ match[2] }`
		}

		date_from_text( date: string ) {
			const [ year, month, day ] = this.date_day( date ).split( '-' ).map( Number )
			return new Date( year, month - 1, day )
		}

		date_fit( date: string ) {
			const time = this.date_time( date )
			const match = this.date_day( date ).match( /^(\d{4})-(\d{2})-(\d{2})$/ )
			if( !match ) return date

			const year = Number( match[1] )
			const month = Math.min( 12, Math.max( 1, Number( match[2] ) ) )
			const day_max = new Date( year, month, 0 ).getDate()
			const day = Math.min( day_max, Math.max( 1, Number( match[3] ) ) )

			const result = [
				String( year ).padStart( 4, '0' ),
				String( month ).padStart( 2, '0' ),
				String( day ).padStart( 2, '0' ),
			].join( '-' )
			return time ? `${ result } ${ time }` : result
		}

		date_anchor() {
			const max = this.date_max()
			return max ? this.date_from_text( max ) : new Date()
		}

		date_week_start( date: Date ) {
			const start = new Date( date )
			const day = ( start.getDay() + 6 ) % 7
			start.setDate( start.getDate() - day )
			return start
		}

		period_from_dates( from: Date, to: Date ) {
			return this.period_clamp({
				from: this.date_text( from ),
				to: this.date_text( to ),
			})
		}

		date_clamp( date: string ) {
			if( !date ) return date
			if( !/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2})?$/.test( date ) ) return date
			date = this.date_fit( date )
			const min = this.date_min()
			const max = this.date_max()
			const day = this.date_day( date )
			if( min && day < min ) return min
			if( max && day > max ) return max
			return date
		}

		@ $mol_mem
		date_from( next?: string ): string {
			return this.period( next ? { from: next, to: this.date_to() } : undefined ).from ?? ''
		}

		@ $mol_mem
		date_to( next?: string ): string {
			return this.period( next ? { to: next, from: this.date_from() } : undefined ).to ?? ''
		}

		period_clamp( period: $shm_ba_data_period ) {
			let from = this.date_clamp( period.from )
			let to = this.date_clamp( period.to )
			if( from && to && from > to ) {
				if( period.from !== from ) to = from
				else from = to
			}
			return { from, to }
		}

		period( next?: $shm_ba_data_period ) {
			return super.period( next && this.period_clamp( next ) ) as $shm_ba_data_period
		}

		preset_period( preset: string ) {
			const anchor = this.date_anchor()
			if( preset === 'all' ) return this.period_all()

			if( preset === 'current_week' ) {
				const from = this.date_week_start( anchor )
				const to = new Date( from )
				to.setDate( to.getDate() + 6 )
				return this.period_from_dates( from, to )
			}

			if( preset === 'previous_week' ) {
				const to = this.date_week_start( anchor )
				to.setDate( to.getDate() - 1 )
				const from = new Date( to )
				from.setDate( from.getDate() - 6 )
				return this.period_from_dates( from, to )
			}

			if( preset === 'days7' ) {
				const from = new Date( anchor )
				from.setDate( from.getDate() - 6 )
				return this.period_from_dates( from, anchor )
			}

			if( preset === 'current_month' ) {
				const from = new Date( anchor.getFullYear(), anchor.getMonth(), 1 )
				const to = new Date( anchor.getFullYear(), anchor.getMonth() + 1, 0 )
				return this.period_from_dates( from, to )
			}

			if( preset === 'previous_month' ) {
				const from = new Date( anchor.getFullYear(), anchor.getMonth() - 1, 1 )
				const to = new Date( anchor.getFullYear(), anchor.getMonth(), 0 )
				return this.period_from_dates( from, to )
			}

			if( preset === 'days30' ) {
				const from = new Date( anchor )
				from.setDate( from.getDate() - 29 )
				return this.period_from_dates( from, anchor )
			}

			if( preset === 'current_year' ) {
				const from = new Date( anchor.getFullYear(), 0, 1 )
				const to = new Date( anchor.getFullYear(), 11, 31 )
				return this.period_from_dates( from, to )
			}

			if( preset === 'previous_year' ) {
				const from = new Date( anchor.getFullYear() - 1, 0, 1 )
				const to = new Date( anchor.getFullYear() - 1, 11, 31 )
				return this.period_from_dates( from, to )
			}

			if( preset === 'months12' ) {
				const from = new Date( anchor )
				from.setMonth( from.getMonth() - 12 )
				from.setDate( from.getDate() + 1 )
				return this.period_from_dates( from, anchor )
			}

			return this.period()
		}

		period_matches( left: $shm_ba_data_period, right: $shm_ba_data_period ) {
			return left.from === right.from && left.to === right.to
		}

		preset_options() {
			return {
				all: 'Весь период',
				current_week: 'Текущая неделя',
				previous_week: 'Прошлая неделя',
				days7: '7 дней',
				current_month: 'Текущий месяц',
				previous_month: 'Прошлый месяц',
				days30: '30 дней',
				current_year: 'Текущий год',
				previous_year: 'Прошлый год',
				months12: '12 месяцев',
			}
		}

		preset_rows() {
			return Object.keys( this.preset_options() ).map( id => this.Preset( id ) )
		}

		option_label( id: string ) {
			const options = this.preset_options() as Record< string, string >
			return id === 'custom' ? 'Произвольный' : options[ id ] ?? id
		}

		override preset_pick( id: string, next?: Event ) {
			if( next === undefined ) return null
			this.period( this.preset_period( id ) )
			this.Date_from().showed( false )
			this.Date_to().showed( false )
			return next
		}

		preset() {
			const period = this.period() as $shm_ba_data_period
			for( const preset of Object.keys( this.preset_options() ) ) {
				if( this.period_matches( period, this.preset_period( preset ) ) ) return preset
			}
			return 'custom'
		}

		date_from_picker_content() {
			return [
				this.Presets_list(),
				this.Date_from_calendar_column(),
			] as readonly $mol_view[]
		}

		date_to_picker_content() {
			return [
				this.Presets_list(),
				this.Date_to_calendar_column(),
			] as readonly $mol_view[]
		}

		date_from_calendar_content() {
			return [
				this.Date_from().Input_row(),
				this.Date_from().Calendar(),
			] as readonly $mol_view[]
		}

		date_to_calendar_content() {
			return [
				this.Date_to().Input_row(),
				this.Date_to().Calendar(),
			] as readonly $mol_view[]
		}

	}

}
